import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Truck, Package, Building2, Stethoscope, RefreshCw, CheckCircle, Clock, 
  MapPin, User, Edit, Trash2, Eye, Settings, Filter, DollarSign,
  AlertTriangle, Check, X, ChevronRight, Plus, FileText, Shield,
  Navigation, Camera, PenTool
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import api from '../../services/api';

const CourierTab = () => {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('jobs'); // jobs, settings, workers, logs
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [editingPricing, setEditingPricing] = useState(false);
  const [pricingForm, setPricingForm] = useState({
    commercial_base_price: 25,
    property_base_price: 30,
    medical_base_price: 45,
    mileage_rate: 0.65,
    free_miles: 15,
    priority_surcharge: 15,
    urgent_surcharge: 30,
    base_address: '398 Tolbert Place, Tarboro, NC'
  });

  useEffect(() => {
    fetchData();
  }, [filter, typeFilter]);

  const fetchData = async () => {
    try {
      let params = [];
      if (filter) params.push(`status=${filter}`);
      if (typeFilter) params.push(`service_type=${typeFilter}`);
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      
      const [jobsRes, statsRes, pricingRes, certsRes] = await Promise.all([
        api.get(`/courier/jobs${queryString}`),
        api.get('/courier/jobs/stats'),
        api.get('/courier/settings/pricing'),
        api.get('/courier/workers/certifications')
      ]);
      
      setJobs(jobsRes.data);
      setStats(statsRes.data);
      setPricing(pricingRes.data);
      setPricingForm(pricingRes.data);
      setCertifications(certsRes.data);
    } catch (error) {
      console.error('Failed to fetch courier data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (jobId) => {
    setProcessing(true);
    try {
      await api.put(`/courier/jobs/${jobId}/approve`);
      toast.success('Medical courier job approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (jobId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    
    setProcessing(true);
    try {
      await api.put(`/courier/jobs/${jobId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Medical courier job rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const handleArchive = async (jobId) => {
    if (!window.confirm('Archive this courier job?')) return;
    
    setProcessing(true);
    try {
      await api.delete(`/courier/jobs/${jobId}`);
      toast.success('Courier job archived');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive');
    } finally {
      setProcessing(false);
    }
  };

  const handleSavePricing = async () => {
    setProcessing(true);
    try {
      await api.put('/courier/settings/pricing', pricingForm);
      toast.success('Pricing settings saved');
      setEditingPricing(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateCertification = async (workerId, field, value) => {
    try {
      const cert = certifications.find(c => c.worker_id === workerId) || { worker_id: workerId };
      const updated = { ...cert, [field]: value };
      await api.put(`/courier/workers/${workerId}/certification`, updated);
      toast.success('Certification updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update certification');
    }
  };

  const getServiceIcon = (type) => {
    switch(type) {
      case 'commercial_courier': return <Package className="w-5 h-5 text-[#D4AF37]" />;
      case 'property_management': return <Building2 className="w-5 h-5 text-amber-500" />;
      case 'medical_courier': return <Stethoscope className="w-5 h-5 text-[var(--error)]" />;
      default: return <Truck className="w-5 h-5" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-[#D4AF37]/10 text-[#D4AF37]',
      assigned: 'bg-[#F4C2C2]/15 text-purple-700',
      picked_up: 'bg-amber-100 text-amber-700',
      in_transit: 'bg-cyan-100 text-cyan-700',
      delivered: 'bg-[var(--success)]/15 text-green-700',
      completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-[var(--error)]/15 text-red-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-[var(--background-subtle)]'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-12 text-center" data-testid="courier-loading">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading courier services...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="courier-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Truck className="w-6 h-6 text-[var(--primary)]" />
            Courier Services
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Manage commercial, property, and medical courier jobs
          </p>
        </div>
        <div className="flex items-center gap-2">
          {['jobs', 'workers', 'settings', 'logs'].map(v => (
            <Button
              key={v}
              variant={view === v ? 'default' : 'outline'}
              size="sm"
              onClick={() => setView(v)}
            >
              {v === 'jobs' && <Package className="w-4 h-4 mr-1" />}
              {v === 'workers' && <User className="w-4 h-4 mr-1" />}
              {v === 'settings' && <Settings className="w-4 h-4 mr-1" />}
              {v === 'logs' && <FileText className="w-4 h-4 mr-1" />}
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </Button>
          ))}
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {view === 'jobs' && (
        <>
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="card p-4 border-l-4 border-blue-400">
                <p className="text-3xl font-bold text-[#D4AF37]">{stats.total_jobs || 0}</p>
                <p className="text-xs text-[var(--text-muted)]">Total Jobs</p>
              </div>
              <div className="card p-4 border-l-4 border-green-400">
                <p className="text-3xl font-bold text-[var(--success)]">${stats.total_revenue?.toFixed(0) || 0}</p>
                <p className="text-xs text-[var(--text-muted)]">Total Revenue</p>
              </div>
              <div className="card p-4 border-l-4 border-amber-400">
                <p className="text-3xl font-bold text-amber-600">{stats.by_status?.new?.count || 0}</p>
                <p className="text-xs text-[var(--text-muted)]">New Jobs</p>
              </div>
              <div className="card p-4 border-l-4 border-purple-400">
                <p className="text-3xl font-bold text-[#F4C2C2]">{stats.by_status?.in_transit?.count || 0}</p>
                <p className="text-xs text-[var(--text-muted)]">In Transit</p>
              </div>
              <div className="card p-4 border-l-4 border-red-400">
                <p className="text-3xl font-bold text-[var(--error)]">{stats.pending_medical_approvals || 0}</p>
                <p className="text-xs text-[var(--text-muted)]">Pending Approvals</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <div className="flex gap-1">
              {['', 'new', 'assigned', 'in_transit', 'delivered', 'completed'].map(s => (
                <Button
                  key={s}
                  variant={filter === s ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(s)}
                >
                  {s === '' ? 'All' : s.replace('_', ' ')}
                </Button>
              ))}
            </div>
            <div className="flex gap-1 border-l pl-2">
              {['', 'commercial_courier', 'property_management', 'medical_courier'].map(t => (
                <Button
                  key={t}
                  variant={typeFilter === t ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTypeFilter(t)}
                >
                  {t === '' ? 'All Types' : t === 'commercial_courier' ? 'Commercial' : t === 'property_management' ? 'Property' : 'Medical'}
                </Button>
              ))}
            </div>
          </div>

          {/* Jobs List */}
          <div className="space-y-4">
            {jobs.length === 0 ? (
              <div className="card p-12 text-center">
                <Truck className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
                <h3 className="text-lg font-bold mb-2">No Courier Jobs</h3>
                <p className="text-[var(--text-muted)]">Courier jobs will appear here when customers book.</p>
              </div>
            ) : (
              jobs.map(job => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`card p-5 ${job.requires_approval && !job.approved_by ? 'border-2 border-red-300' : ''}`}
                  data-testid={`courier-job-${job.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getServiceIcon(job.service_type)}
                        {getStatusBadge(job.status)}
                        {job.urgency_level === 'urgent' && (
                          <span className="px-2 py-0.5 bg-[var(--error)]/15 text-red-700 rounded-full text-xs">URGENT</span>
                        )}
                        {job.urgency_level === 'priority' && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">PRIORITY</span>
                        )}
                        {job.is_medical && (
                          <span className="px-2 py-0.5 bg-[var(--error)]/10 text-[var(--error)] rounded-full text-xs flex items-center gap-1">
                            <Stethoscope className="w-3 h-3" /> Medical
                          </span>
                        )}
                        {job.requires_approval && !job.approved_by && (
                          <span className="px-2 py-0.5 bg-[var(--warning)]/15 text-yellow-700 rounded-full text-xs">
                            Needs Approval
                          </span>
                        )}
                      </div>
                      
                      <h4 className="font-bold text-lg">{job.item_description}</h4>
                      <p className="text-sm text-[var(--text-muted)] mb-3">
                        {job.job_type?.replace('_', ' ')} • {job.customer_name}
                      </p>
                      
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div className="p-3 bg-[#D4AF37]/10 rounded-lg">
                          <p className="font-medium flex items-center gap-1 text-[#D4AF37]">
                            <MapPin className="w-4 h-4" /> Pickup
                          </p>
                          <p className="text-[var(--text-secondary)]">{job.pickup_address}</p>
                          <p className="text-xs text-[var(--text-muted)]">{job.pickup_contact_name} • {job.pickup_time_window}</p>
                        </div>
                        <div className="p-3 bg-[var(--success)]/10 rounded-lg">
                          <p className="font-medium flex items-center gap-1 text-green-700">
                            <Navigation className="w-4 h-4" /> Delivery
                          </p>
                          <p className="text-[var(--text-secondary)]">{job.delivery_address}</p>
                          <p className="text-xs text-[var(--text-muted)]">{job.delivery_contact_name} • {job.delivery_time_window}</p>
                        </div>
                      </div>
                      
                      {job.detailed_requirements && (
                        <div className="mt-3 p-3 bg-[var(--background-subtle)] rounded-lg">
                          <p className="text-sm font-medium">Requirements:</p>
                          <p className="text-sm text-[var(--text-secondary)]">{job.detailed_requirements}</p>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 mt-3 text-xs text-[var(--text-muted)]">
                        <span>Scheduled: {new Date(job.scheduled_date).toLocaleDateString()}</span>
                        {job.worker_name && <span>Driver: {job.worker_name}</span>}
                        {job.final_amount && <span className="font-bold text-[var(--success)]">${job.final_amount}</span>}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <Button variant="outline" size="sm" onClick={() => setSelectedJob(job)}>
                        <Eye className="w-4 h-4 mr-1" /> Details
                      </Button>
                      {job.requires_approval && !job.approved_by && (
                        <>
                          <Button 
                            size="sm" 
                            onClick={() => handleApprove(job.id)}
                            disabled={processing}
                            className="bg-[var(--success)] hover:bg-[var(--success)]/80 text-white"
                          >
                            <Check className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleReject(job.id)}
                            disabled={processing}
                          >
                            <X className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleArchive(job.id)}
                        className="text-[var(--error)]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </>
      )}

      {view === 'workers' && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Worker Courier Certifications
          </h3>
          
          {certifications.length === 0 ? (
            <div className="card p-8 text-center">
              <User className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-3" />
              <p>No worker certifications yet</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {certifications.map(cert => (
                <div key={cert.worker_id} className="card p-4" data-testid={`cert-${cert.worker_id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-bold">{cert.worker_name}</p>
                      <p className="text-sm text-[var(--text-muted)]">
                        {cert.is_courier_certified ? '✓ Certified' : 'Not Certified'}
                      </p>
                    </div>
                    <Switch
                      checked={cert.is_courier_certified}
                      onCheckedChange={(v) => handleUpdateCertification(cert.worker_id, 'is_courier_certified', v)}
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 bg-[#D4AF37]/10 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Commercial</span>
                        <Switch
                          checked={cert.commercial_courier_enabled}
                          onCheckedChange={(v) => handleUpdateCertification(cert.worker_id, 'commercial_courier_enabled', v)}
                        />
                      </div>
                    </div>
                    <div className="p-3 bg-[var(--warning)]/10 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Property</span>
                        <Switch
                          checked={cert.property_management_enabled}
                          onCheckedChange={(v) => handleUpdateCertification(cert.worker_id, 'property_management_enabled', v)}
                        />
                      </div>
                    </div>
                    <div className="p-3 bg-[var(--error)]/10 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Medical</span>
                        <Switch
                          checked={cert.medical_courier_enabled}
                          onCheckedChange={(v) => handleUpdateCertification(cert.worker_id, 'medical_courier_enabled', v)}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 text-xs text-[var(--text-muted)] flex gap-4">
                    <span>{cert.courier_agreement_signed ? '✓ Agreement Signed' : '✗ Agreement Pending'}</span>
                    <span>{cert.training_completed ? '✓ Training Complete' : '✗ Training Pending'}</span>
                    <span>{cert.identity_verified ? '✓ ID Verified' : '✗ ID Not Verified'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'settings' && (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Pricing Settings
              </h3>
              {!editingPricing && (
                <Button variant="outline" onClick={() => setEditingPricing(true)}>
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
              )}
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Commercial Base Price ($)</label>
                <Input
                  type="number"
                  value={pricingForm.commercial_base_price}
                  onChange={(e) => setPricingForm({...pricingForm, commercial_base_price: parseFloat(e.target.value)})}
                  disabled={!editingPricing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Property Base Price ($)</label>
                <Input
                  type="number"
                  value={pricingForm.property_base_price}
                  onChange={(e) => setPricingForm({...pricingForm, property_base_price: parseFloat(e.target.value)})}
                  disabled={!editingPricing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Medical Base Price ($)</label>
                <Input
                  type="number"
                  value={pricingForm.medical_base_price}
                  onChange={(e) => setPricingForm({...pricingForm, medical_base_price: parseFloat(e.target.value)})}
                  disabled={!editingPricing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Mileage Rate ($/mile)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={pricingForm.mileage_rate}
                  onChange={(e) => setPricingForm({...pricingForm, mileage_rate: parseFloat(e.target.value)})}
                  disabled={!editingPricing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Free Miles (included)</label>
                <Input
                  type="number"
                  value={pricingForm.free_miles}
                  onChange={(e) => setPricingForm({...pricingForm, free_miles: parseFloat(e.target.value)})}
                  disabled={!editingPricing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Base Location</label>
                <Input
                  value={pricingForm.base_address}
                  onChange={(e) => setPricingForm({...pricingForm, base_address: e.target.value})}
                  disabled={!editingPricing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Priority Surcharge ($)</label>
                <Input
                  type="number"
                  value={pricingForm.priority_surcharge}
                  onChange={(e) => setPricingForm({...pricingForm, priority_surcharge: parseFloat(e.target.value)})}
                  disabled={!editingPricing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Urgent Surcharge ($)</label>
                <Input
                  type="number"
                  value={pricingForm.urgent_surcharge}
                  onChange={(e) => setPricingForm({...pricingForm, urgent_surcharge: parseFloat(e.target.value)})}
                  disabled={!editingPricing}
                />
              </div>
            </div>
            
            {editingPricing && (
              <div className="flex gap-3 mt-6 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditingPricing(false)}>Cancel</Button>
                <Button onClick={handleSavePricing} disabled={processing} className="btn-primary">
                  Save Pricing
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {view === 'logs' && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Delivery Logs & Compliance
          </h3>
          
          <div className="card p-4">
            <p className="text-[var(--text-muted)]">
              Delivery logs show all completed jobs with pickup/delivery timestamps, 
              proof of delivery photos, signatures, and chain of custody for medical items.
            </p>
          </div>
          
          <div className="space-y-3">
            {jobs.filter(j => ['delivered', 'completed'].includes(j.status)).map(job => (
              <div key={job.id} className="card p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      {getServiceIcon(job.service_type)}
                      <span className="font-medium">{job.item_description}</span>
                      {job.is_medical && <span className="text-xs bg-[var(--error)]/15 text-[var(--error)] px-2 py-0.5 rounded">Medical</span>}
                    </div>
                    <p className="text-sm text-[var(--text-muted)]">
                      {job.worker_name} • {new Date(job.delivered_at || job.updated_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {job.pickup_photo_url && <Camera className="w-4 h-4 text-[#D4AF37]" title="Pickup Photo" />}
                    {job.delivery_photo_url && <Camera className="w-4 h-4 text-[var(--success)]" title="Delivery Photo" />}
                    {job.signature_url && <PenTool className="w-4 h-4 text-[#F4C2C2]" title="Signature" />}
                    {job.chain_of_custody_required && <Shield className="w-4 h-4 text-[var(--error)]" title="Chain of Custody" />}
                  </div>
                </div>
                {job.custody_log && job.custody_log.length > 0 && (
                  <div className="mt-3 p-3 bg-[var(--error)]/10 rounded-lg">
                    <p className="text-xs font-medium text-red-700 mb-2">Chain of Custody:</p>
                    {job.custody_log.map((entry) => (
                      <div key={`${entry.timestamp}-${entry.action}`} className="text-xs text-[var(--error)]">
                        {new Date(entry.timestamp).toLocaleString()} - {entry.action} by {entry.handler_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Job Details Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                {getServiceIcon(selectedJob.service_type)}
                Courier Job Details
              </h2>
              <Button variant="ghost" onClick={() => setSelectedJob(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-[var(--accent)] rounded-xl">
                <p className="font-bold text-lg">{selectedJob.item_description}</p>
                <p className="text-sm text-[var(--text-muted)]">
                  {selectedJob.job_type?.replace('_', ' ')} • {getStatusBadge(selectedJob.status)}
                </p>
              </div>
              
              {selectedJob.detailed_requirements && (
                <div className="p-4 bg-[var(--warning)]/10 rounded-xl">
                  <p className="font-medium text-[var(--warning)] mb-1">Customer Requirements:</p>
                  <p className="text-[var(--text-secondary)]">{selectedJob.detailed_requirements}</p>
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-[#D4AF37]/10 rounded-xl">
                  <h4 className="font-bold text-[#D4AF37] mb-2">Pickup Details</h4>
                  <p>{selectedJob.pickup_address}</p>
                  <p className="text-sm">{selectedJob.pickup_contact_name}</p>
                  <p className="text-sm">{selectedJob.pickup_contact_phone}</p>
                  {selectedJob.pickup_instructions && (
                    <p className="text-xs mt-2 text-[#D4AF37]">{selectedJob.pickup_instructions}</p>
                  )}
                </div>
                <div className="p-4 bg-[var(--success)]/10 rounded-xl">
                  <h4 className="font-bold text-green-700 mb-2">Delivery Details</h4>
                  <p>{selectedJob.delivery_address}</p>
                  <p className="text-sm">{selectedJob.delivery_contact_name}</p>
                  <p className="text-sm">{selectedJob.delivery_contact_phone}</p>
                  {selectedJob.delivery_instructions && (
                    <p className="text-xs mt-2 text-[var(--success)]">{selectedJob.delivery_instructions}</p>
                  )}
                </div>
              </div>
              
              {selectedJob.custody_log && selectedJob.custody_log.length > 0 && (
                <div className="p-4 bg-[var(--error)]/10 rounded-xl">
                  <h4 className="font-bold text-red-700 mb-2">Chain of Custody Log</h4>
                  {selectedJob.custody_log.map((entry) => (
                    <div key={`${entry.timestamp}-${entry.action}`} className="py-2 border-b border-red-100 last:border-0">
                      <p className="text-sm font-medium">{entry.action.replace('_', ' ')}</p>
                      <p className="text-xs text-[var(--error)]">
                        {new Date(entry.timestamp).toLocaleString()} by {entry.handler_name}
                      </p>
                      {entry.notes && <p className="text-xs mt-1">{entry.notes}</p>}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex justify-between items-center p-4 bg-[var(--background-subtle)] rounded-xl">
                <div>
                  <p className="text-sm text-[var(--text-muted)]">Final Amount</p>
                  <p className="text-2xl font-bold text-[var(--success)]">${selectedJob.final_amount || selectedJob.base_price || 0}</p>
                </div>
                {selectedJob.worker_name && (
                  <div className="text-right">
                    <p className="text-sm text-[var(--text-muted)]">Assigned Driver</p>
                    <p className="font-medium">{selectedJob.worker_name}</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setSelectedJob(null)} className="flex-1">
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CourierTab;
